import { useEffect, useState, useRef } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { Close, MenuBook } from "@mui/icons-material";
import api from "../../../api";

/**
 * PaperPageView — renders past-paper thumbnails alongside extracted question cards,
 * grouped by page_number. Fetches thumbnails from /admin/import/{id}/page-thumbnails.
 *
 * Each page shows:
 *   Left:  JPEG thumbnail of the original paper page (from DataLab /thumbnails)
 *   Right: List of question paper_numbers on that page (passed as props)
 *
 * Questions without a page_number appear in a separate "Unlocated" section at the bottom.
 */
export default function PaperPageView({ importId, questions, onQuestionClick, onClose }) {
  const [thumbnails, setThumbnails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePage, setActivePage] = useState(null);
  const pageRefs = useRef({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    api
      .get(`/admin/import/${importId}/page-thumbnails`)
      .then((res) => {
        if (!cancelled) {
          setThumbnails(res.data?.data?.thumbnails ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) setError("Thumbnails unavailable — no checkpoint for this import.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [importId]);

  // Group questions by page_number (0-indexed, matching thumbnail index)
  const byPage = {};
  const unlocated = [];
  for (const q of questions ?? []) {
    const pg = q.page_number ?? q.page ?? null;
    if (pg !== null && pg !== undefined) {
      (byPage[pg] = byPage[pg] ?? []).push(q);
    } else {
      unlocated.push(q);
    }
  }

  const pageNums = Object.keys(byPage)
    .map(Number)
    .sort((a, b) => a - b);

  const scrollToPage = (pg) => {
    setActivePage(pg);
    pageRefs.current[pg]?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderLeft: "1px solid",
        borderColor: "divider",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1.5,
          py: 0.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "grey.50",
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <MenuBook sx={{ fontSize: 16, color: "text.secondary" }} />
          <Typography variant="subtitle2" sx={{ fontSize: "0.8rem", color: "text.secondary" }}>
            Paper View
          </Typography>
          {!loading && thumbnails.length > 0 && (
            <Typography variant="caption" color="text.disabled">
              ({thumbnails.length} pages)
            </Typography>
          )}
        </Box>
        <Tooltip title="Close paper view">
          <IconButton size="small" onClick={onClose}>
            <Close fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Page-jump strip */}
      {pageNums.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.5,
            px: 1.5,
            py: 0.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "grey.50",
            flexShrink: 0,
          }}
        >
          {pageNums.map((pg) => (
            <Box
              key={pg}
              onClick={() => scrollToPage(pg)}
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 1,
                fontSize: "0.7rem",
                fontWeight: activePage === pg ? 700 : 400,
                bgcolor: activePage === pg ? "primary.main" : "grey.200",
                color: activePage === pg ? "white" : "text.primary",
                cursor: "pointer",
                "&:hover": { bgcolor: activePage === pg ? "primary.dark" : "grey.300" },
              }}
            >
              p{pg + 1}
            </Box>
          ))}
        </Box>
      )}

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 1.5, py: 1 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, fontStyle: "italic" }}>
            {error}
          </Typography>
        )}

        {!loading && !error && pageNums.length === 0 && unlocated.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
            No page location data available. Re-extract to populate.
          </Typography>
        )}

        {/* Pages with thumbnails and question lists */}
        {pageNums.map((pg) => {
          const thumb = thumbnails[pg] ?? null;
          const pageQuestions = byPage[pg] ?? [];

          return (
            <Box
              key={pg}
              ref={(el) => (pageRefs.current[pg] = el)}
              sx={{ mb: 2.5 }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ display: "block", mb: 0.75 }}
              >
                Page {pg + 1}
              </Typography>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                {/* Thumbnail */}
                <Box
                  sx={{
                    flexShrink: 0,
                    width: 140,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                    bgcolor: "grey.100",
                  }}
                >
                  {thumb ? (
                    <img
                      src={thumb}
                      alt={`Page ${pg + 1}`}
                      style={{ width: "100%", display: "block" }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 100,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography variant="caption" color="text.disabled">
                        No image
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Question list */}
                <Box sx={{ flex: 1 }}>
                  {pageQuestions.map((q) => (
                    <Box
                      key={q.paper_number}
                      onClick={() => onQuestionClick?.(q.paper_number)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1,
                        py: 0.5,
                        mb: 0.5,
                        borderRadius: 1,
                        cursor: "pointer",
                        border: "1px solid",
                        borderColor: "divider",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        sx={{ minWidth: 28, color: "primary.main" }}
                      >
                        Q{q.paper_number}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                        }}
                      >
                        {q.question_text?.slice(0, 80) ?? "(no text)"}
                      </Typography>
                      {q._datalab_visual_hint && (
                        <Typography
                          variant="caption"
                          sx={{
                            bgcolor: "warning.50",
                            color: "warning.dark",
                            border: "1px solid",
                            borderColor: "warning.200",
                            px: 0.5,
                            borderRadius: 0.5,
                            fontSize: "0.65rem",
                            flexShrink: 0,
                          }}
                        >
                          img needed
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Divider sx={{ mt: 2 }} />
            </Box>
          );
        })}

        {/* Unlocated questions */}
        {unlocated.length > 0 && (
          <Box>
            <Typography
              variant="caption"
              fontWeight={700}
              color="text.disabled"
              sx={{ display: "block", mb: 0.75 }}
            >
              Unlocated ({unlocated.length})
            </Typography>
            {unlocated.map((q) => (
              <Box
                key={q.paper_number}
                onClick={() => onQuestionClick?.(q.paper_number)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1,
                  py: 0.5,
                  mb: 0.5,
                  borderRadius: 1,
                  cursor: "pointer",
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "grey.50",
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                <Typography variant="caption" fontWeight={700} sx={{ minWidth: 28 }}>
                  Q{q.paper_number}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {q.question_text?.slice(0, 80) ?? "(no text)"}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

export function PaperViewToggleButton({ show, onToggle }) {
  return (
    <Tooltip title={show ? "Hide paper view" : "Show past-paper page view"}>
      <IconButton onClick={onToggle} color={show ? "primary" : "default"} size="small">
        <MenuBook />
      </IconButton>
    </Tooltip>
  );
}
